import { Link, useLocation } from "react-router-dom";
import { useState, useMemo, useCallback } from "react";
import { BrandLogo } from "@/components/ui/brand-logo";
import { PanelLeftClose, PanelLeftOpen, Search } from "lucide-react";

import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useMenuAccess } from "@/hooks/shared/use-menu-access";
import { cn } from "@/lib/utils";
import { useCommandPalette } from "@/components/search/command-palette-provider";
import { useSidebar } from "./sidebar-context";
import { isMenuSubGroup, type MenuPath, type MenuSubGroup } from "@/lib/menu-paths";
import { SectionGroup, getSectionIdForPath, isPathActive } from "./sidebar-sections";

/** O modificador do sistema: ⌘ no Mac, Ctrl no resto. */
const MOD_KEY =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl";

// ── AppSidebar ────────────────────────────────────────────────────────────────

interface AppSidebarProps {
  /** Fechar o painel no telemóvel ao navegar. */
  onItemClick?: () => void;
  /** No telemóvel a sidebar abre sempre inteira, mesmo colapsada no desktop. */
  forceExpanded?: boolean;
}

export function AppSidebar({ onItemClick, forceExpanded = false }: AppSidebarProps) {
  const { pathname } = useLocation();
  const { hasMenuAccess, accessibleMenuSections } = useMenuAccess();
  const { open: openCommandPalette } = useCommandPalette();
  const { collapsed: collapsedPref, toggle } = useSidebar();

  const collapsed = forceExpanded ? false : collapsedPref;

  const activeSectionId = getSectionIdForPath(pathname);
  // Acordeão: além da secção onde se está (sempre aberta), só uma outra fica aberta de cada vez. Com quarenta
  // secções abertas ao mesmo tempo o menu passava de um ecrã e perdia-se o sítio.
  const [openId, setOpenId] = useState<string | null>(null);

  const toggleSection = useCallback((id: string) => {
    // Secção ativa nunca pode ser fechada
    if (id === activeSectionId) return;
    setOpenId((prev) => (prev === id ? null : id));
  }, [activeSectionId]);

  const isSectionExpanded = useCallback(
    (id: string): boolean => id === activeSectionId || id === openId,
    [activeSectionId, openId]
  );

  const isActive = useCallback(
    (path: string) => isPathActive(pathname, path),
    [pathname]
  );

  const visibleSections = useMemo(() => {
    return accessibleMenuSections
      .map((section) => {
        const filteredItems = section.items
          .map((item): MenuPath | MenuSubGroup | null => {
            if (isMenuSubGroup(item)) {
              const visible = item.items.filter((si) => hasMenuAccess(si.path));
              return visible.length > 0 ? { ...item, items: visible } : null;
            }
            return hasMenuAccess(item.path) ? item : null;
          })
          .filter((item): item is MenuPath | MenuSubGroup => item !== null);
        return { section, items: filteredItems };
      })
      .filter(({ items }) => items.length > 0);
  }, [accessibleMenuSections, hasMenuAccess]);

  return (
    // Sem cor própria: a barra assenta no fundo da moldura (a `DashboardLayout`
    // é que a pinta). É esse degrau de fundo — e não uma linha — que a separa
    // do conteúdo.
    <div className="flex h-full flex-col">
      {/* Cabeçalho: marca + colapsar */}
      <div
        className={cn(
          "flex h-14 shrink-0 items-center",
          collapsed ? "justify-center px-2" : "gap-2 pl-4 pr-2"
        )}
      >
        <Link
          to="/dashboard"
          onClick={onItemClick}
          className={cn(
            "flex min-w-0 items-center transition-opacity hover:opacity-70",
            collapsed && "hidden"
          )}
        >
          {/* `max-w` + `object-contain`: o logo é horizontal e largo — sem isto
              o botão de colapsar empurra-o e a marca sai cortada. */}
          <BrandLogo className="h-7 w-auto max-w-[168px] object-contain object-left" fallbackClassName="text-base" />
        </Link>

        {!forceExpanded && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={toggle}
                aria-label={collapsed ? "Expandir menu" : "Colapsar menu"}
                className={cn(
                  "grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted-foreground/60",
                  "transition-colors hover:bg-foreground/5 hover:text-foreground",
                  !collapsed && "ml-auto"
                )}
              >
                {collapsed ? (
                  <PanelLeftOpen className="h-[17px] w-[17px]" strokeWidth={1.75} />
                ) : (
                  <PanelLeftClose className="h-[17px] w-[17px]" strokeWidth={1.75} />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={10}>
              {collapsed ? "Expandir menu" : "Colapsar menu"}{" "}
              <span className="ml-1 text-muted-foreground">{MOD_KEY}\</span>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Pesquisa global — abre o command palette (⌘K). Pesquisa toda a plataforma.
          No desktop expandido a pesquisa vive no cabeçalho do topo; aqui fica
          só quando o topo não a mostra (telemóvel) ou quando não há largura
          para rótulos (colapsada). */}
      <div className={cn("shrink-0 px-2 pb-1", collapsed ? "" : "lg:hidden")}>
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => openCommandPalette()}
                aria-label="Pesquisar"
                className="grid h-9 w-9 place-items-center rounded-lg text-muted-foreground/70 transition-colors hover:bg-foreground/[0.035] hover:text-foreground"
              >
                <Search className="h-[17px] w-[17px]" strokeWidth={1.75} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={10}>
              Pesquisar <span className="ml-1 text-muted-foreground">{MOD_KEY}K</span>
            </TooltipContent>
          </Tooltip>
        ) : (
          <SearchTrigger onClick={() => openCommandPalette()} />
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="min-h-0 flex-1">
        <nav className={cn("space-y-0.5 py-1", collapsed ? "px-2" : "px-2")}>
          {visibleSections.map(({ section, items }, idx) => (
            <div key={section.id}>
              {idx > 0 && (
                <div className={cn("h-px bg-border/50", collapsed ? "mx-2 my-1.5" : "mx-2.5 my-1.5")} />
              )}
              <SectionGroup
                section={section}
                items={items}
                isExpanded={isSectionExpanded(section.id)}
                onToggle={() => toggleSection(section.id)}
                isActive={isActive}
                onItemClick={onItemClick}
                pathname={pathname}
                collapsed={collapsed}
              />
            </div>
          ))}
        </nav>
      </ScrollArea>

    </div>
  );
}

/**
 * O botão que abre a pesquisa. Parece um campo de texto de propósito — quem
 * chega quer escrever, e um campo convida a isso melhor do que um botão.
 * Exportado porque o cabeçalho do topo mostra o mesmo no desktop.
 */
export const SearchTrigger = ({ onClick, className }: { onClick: () => void; className?: string }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "group flex h-9 w-full items-center gap-2.5 rounded-lg border border-border/60 bg-background px-3",
      "text-[13px] text-muted-foreground transition-all duration-150",
      // O hover levanta ligeiramente o campo, como um botão físico: a borda
      // ganha corpo e aparece uma sombra rasa. Chega para o campo responder
      // sem tirar os olhos do conteúdo.
      "hover:border-border hover:text-foreground hover:shadow-[0_1px_3px_rgba(0,0,0,0.05)]",
      "focus-visible:border-primary focus-visible:outline-hidden focus-visible:ring-4 focus-visible:ring-primary/10",
      className
    )}
  >
    <Search
      className="h-4 w-4 shrink-0 text-muted-foreground/50 transition-colors group-hover:text-muted-foreground"
      strokeWidth={1.75}
    />
    <span className="truncate">Pesquisar…</span>
    <span className="ml-auto flex shrink-0 items-center gap-1">
      <Kbd>{MOD_KEY}</Kbd>
      <Kbd>K</Kbd>
    </span>
  </button>
);

const Kbd = ({ children }: { children: React.ReactNode }) => (
  <kbd className="grid h-[18px] min-w-[18px] place-items-center rounded border border-border/60 bg-background px-1 font-sans text-[10px] font-medium leading-none text-muted-foreground/70">
    {children}
  </kbd>
);
